import styled, {css} from "styled-components";
import {colorPalette} from "../styles/colorPalette";
import {baseDistance, ELLIPSIS, Flex, fontSize, fontWeight, radius} from "../styles/styles";
import {PillStyled, SelectInputStyled} from "../input/select/SelectInputV5";
import {IconButtonStyled} from "../interaction/IconButtonV5";
import {DateTimeInputStyled} from "../input/datetime/DateTimeInputV5";
import {PropagatedFieldInDetailsContent} from "./datatypes/SingleFieldInput.styled";
import {MultiSelectInputStyled} from "./datatypes/multi/MultiSelect.styled";

export const FieldElementsContainerStyled = styled.div`
	display: flex;
	align-items: flex-start;
	width: 100%;
	min-width: 0;

	.label {
		display: flex;
		align-items: center;
		gap: ${baseDistance.xs};
		padding-right: ${baseDistance.sm};
		height: 32px;

		span {
			${ELLIPSIS}
			height: 16px;
		}
	}

	.element {
		pointer-events: auto;
		align-self: center;

		${MultiSelectInputStyled}, ${SelectInputStyled}, ${SelectInputStyled} ${PillStyled} {
			width: 100%;
		}
	}

	${IconButtonStyled} {
		&.isActive {
			svg {
				color: ${colorPalette.white};
			}
		}
	}
`;

export const FieldIconStyled = styled.div<{$initialPosition?: boolean}>`
	position: absolute;
	left: -24px;
	top: 8px;

	${(props) =>
		props.$initialPosition &&
		css`
			position: initial;
		`};
`;

export const FieldStyled = styled.div`
	${Flex};
	font-size: ${fontSize.md};
	align-items: flex-start;
	position: relative;
	height: auto;

	&.Field {
		align-items: flex-start;
		display: flex;

		&.noWrap {
			grid-column: 1/-1;
		}
	}

	input[type="checkbox"] {
		margin-left: 13px;
	}

	.element {
		margin-right: 10px;
		width: calc(100% - 375px);
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: start;
		font-weight: ${fontWeight.thin};

		&.shouldOverflow {
			overflow: inherit;
		}

		// Selector .element-s which has context, but no child.
		&:not(:has(> *)) {
			padding-left: 9px;
		}
	}

	label + .element {
		flex: 1;
	}

	&.decimals .element {
		position: relative;
		display: inline;

		.infoIcon {
			display: inline;
			right: 75px;
		}
	}

	label {
		display: flex;
		align-items: center;
		width: 150px;

		.element {
			font-weight: ${fontWeight.thin};
		}
	}

	&[data-inputtype="MultiLineText"] {
		.unfocused {
			display: block;
			overflow: auto;
			height: 100%;
			max-height: 140px;
			padding: 10px 25px 10px 10px;
		}

		.multiline {
			min-height: 38px;
		}
	}

	&[data-inputtype="Type"] {
		/*
		"Type" and not "SingleLineInput"
		because of the settings in FieldDataTypes.ts
		*/
		.element {
			overflow: auto;
		}
	}

	.TextInput {
		& + svg.icon {
			display: none;
			position: absolute;
			width: 16px;
			height: 16px;
			top: 50%;
			transform: translateY(-50%);
			right: 11px;
		}

		&:hover,
		&:active,
		&:focus {
			& + svg.icon {
				display: block;
			}
		}
	}

	&:hover {
		.element {
			& > *:not(.add-item):not(.ClickToEditInput):not(.SpinnerStyled):not(.disabled):not(.error):not(.ToggleSwitch):not(.isActive) {
				// TODO this rule is bad
				border-color: ${colorPalette.gray.c300};
			}
		}
	}

	&.disabled {
		pointer-events: auto;

		.element {
			color: ${colorPalette.gray.c700Dark};

			.SelectInput,
			.MultiSelectInput {
				border-color: transparent;

				.input::after {
					display: none;
				}
			}
		}

		&:hover {
			.element {
				.SelectInput,
				.MultiSelectInput {
					border-color: transparent;
				}
			}
		}
	}

	&.dol-field {
		position: relative;
		left: -40px;
		width: calc(100% + 16px);

		${FieldIconStyled} {
			position: relative;
			padding-right: ${baseDistance.sm};
			padding-left: ${baseDistance.md};
			left: 0;
		}

		&:hover {
			background-color: ${colorPalette.gray.c100};
			border-radius: ${radius.sm};
			outline: 1px solid ${colorPalette.primary.c500Primary};

			${FieldElementsContainerStyled} .element >:not(${PropagatedFieldInDetailsContent}) >* {
				// Boolean ToggleSwitch should not have white background
				&:not(${Flex}) {
					background-color: ${colorPalette.white};
				}

				&${DateTimeInputStyled}:hover {
					background-color: ${colorPalette.gray.c200Light};
				}

				&.Spinner {
					background-color: transparent;
					border-radius: 50%;
				}
			}
		}

		${FieldElementsContainerStyled} {
			&:has(${SelectInputStyled}),
			&:has(${MultiSelectInputStyled}) {
				width: calc(100% - 40px);
			}
		}
	}
`;
